import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import styles from './AsignaturaPage.module.css';

const SECCIONES = [
  { key: 'anuncios',       label: 'Anuncios',      icon: '📢', desc: 'Novedades y avisos del profesor' },
  { key: 'contenidos',     label: 'Contenidos',     icon: '📁', desc: 'Materiales y recursos del curso' },
  { key: 'trabajos',       label: 'Trabajos',       icon: '📝', desc: 'Tareas y entregas' },
  { key: 'calificaciones', label: 'Calificaciones', icon: '⭐', desc: 'Notas y evaluaciones' },
  { key: 'mensajes',       label: 'Mensajes',       icon: '✉️',  desc: 'Comunicación con el profesor' },
];
const SECCIONES_PROFESOR = [
  { key: 'notas', label: 'Resumen de notas', icon: '📊', desc: 'Notas de todos los alumnos' },
];

export default function AsignaturaPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [asignatura, setAsignatura] = useState(null);
  const [anuncios, setAnuncios]     = useState([]);
  const [trabajos, setTrabajos]     = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/asignaturas/${id}`),
      api.get(`/asignaturas/${id}/anuncios`),
      api.get(`/asignaturas/${id}/trabajos`),
    ]).then(([asigRes, anunciosRes, trabajosRes]) => {
      setAsignatura(asigRes.data.data);
      setAnuncios((anunciosRes.data.data || []).slice(0, 3));
      setTrabajos((trabajosRes.data.data || []).slice(0, 3));
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className={styles.loading}>Cargando...</div>;
  if (!asignatura) return <div className={styles.loading}>Asignatura no encontrada.</div>;

  const vencido = f => f && new Date(f) < new Date();

  const irAnuncio = (anuncioId) => {
    navigate(`/asignaturas/${id}/anuncios`, { state: { anuncioId } });
  };

  const irTrabajo = (trabajoId) => {
    navigate(`/asignaturas/${id}/trabajos`, { state: { trabajoId } });
  };

  return (
    <div className={styles.page}>

      {/* Hero */}
      <div className={styles.hero}>
        <div className={styles.heroContent}>
          <span className={styles.codigo}>{asignatura.codigo}</span>
          <h1 className={styles.nombre}>{asignatura.nombre}</h1>
          {asignatura.descripcion && <p className={styles.desc}>{asignatura.descripcion}</p>}
          <div className={styles.heroBadges}>
            <span className={styles.badge}>Curso {asignatura.curso}º</span>
            <span className={styles.badge}>Cuatrimestre {asignatura.cuatrimestre}</span>
            <span className={styles.badge}>{user?.rol === 'profesor' ? 'Profesor' : 'Alumno'}</span>
          </div>
        </div>
      </div>

      {/* Grid secciones */}
      <div className={styles.seccionesGrid}>
        {SECCIONES.map(s => (
          <button key={s.key}
            className={styles.seccionCard}
            onClick={() => navigate(`/asignaturas/${id}/${s.key}`)}>
            <span className={styles.seccionIcon}>{s.icon}</span>
            <div>
              <p className={styles.seccionLabel}>{s.label}</p>
              <p className={styles.seccionDesc}>{s.desc}</p>
            </div>
            <span className={styles.seccionArrow}>›</span>
          </button>
        ))}
        {user?.rol === 'profesor' && SECCIONES_PROFESOR.map(s => (
          <button key={s.key}
            className={`${styles.seccionCard} ${styles.seccionProfesor}`}
            onClick={() => navigate(`/asignaturas/${id}/${s.key}`)}>
            <span className={styles.seccionIcon}>{s.icon}</span>
            <div>
              <p className={styles.seccionLabel}>{s.label}</p>
              <p className={styles.seccionDesc}>{s.desc}</p>
            </div>
            <span className={styles.seccionArrow}>›</span>
          </button>
        ))}
      </div>

      {/* Panels */}
      <div className={styles.panels}>

        {/* Últimos anuncios */}
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h3 className={styles.panelTitle}>📢 Últimos anuncios</h3>
            <button className={styles.verTodo}
              onClick={() => navigate(`/asignaturas/${id}/anuncios`)}>
              Ver todos →
            </button>
          </div>
          {anuncios.length === 0 ? (
            <p className={styles.panelEmpty}>No hay anuncios todavía.</p>
          ) : (
            <div className={styles.panelList}>
              {anuncios.map(anuncio => (
                <div key={anuncio.id}
                  className={styles.panelItem}
                  onClick={() => irAnuncio(anuncio.id)}>
                  {anuncio.fijado == 1 && <span className={styles.pin}>📌</span>}
                  <div>
                    <p className={styles.panelItemTitle}>{anuncio.titulo}</p>
                    <p className={styles.panelItemMeta}>
                      {anuncio.autor_nombre} {anuncio.autor_apellidos} · {new Date(anuncio.created_at).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Trabajos */}
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h3 className={styles.panelTitle}>📝 Trabajos</h3>
            <button className={styles.verTodo}
              onClick={() => navigate(`/asignaturas/${id}/trabajos`)}>
              Ver todos →
            </button>
          </div>
          {trabajos.length === 0 ? (
            <p className={styles.panelEmpty}>No hay trabajos publicados.</p>
          ) : (
            <div className={styles.panelList}>
              {trabajos.map(trabajo => (
                <div key={trabajo.id}
                  className={styles.panelItem}
                  onClick={() => irTrabajo(trabajo.id)}>
                  <div className={styles.panelItemLeft}>
                    <p className={styles.panelItemTitle}>{trabajo.titulo}</p>
                    {trabajo.fecha_limite && (
                      <p className={`${styles.panelItemMeta} ${vencido(trabajo.fecha_limite) ? styles.vencido : ''}`}>
                        {vencido(trabajo.fecha_limite) ? '⛔ Cerrado' : '⏰ Límite'}: {new Date(trabajo.fecha_limite).toLocaleDateString('es-ES')}
                      </p>
                    )}
                  </div>
                  <span className={`${styles.estadoBadge} ${vencido(trabajo.fecha_limite) ? styles.estadoCerrado : styles.estadoAbierto}`}>
                    {vencido(trabajo.fecha_limite) ? 'Cerrado' : 'Abierto'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
